import { Icon, Modal, Z_INDEX } from '@smartgift/frontend-toolkit';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppLoader } from '@hero/components/atoms';
import { useGetProductDetail } from '@hero/services/product-service/hooks';
import { useNotification } from '@hero/store/hooks';

import { ProductDetail } from '../../ProductDetail';

function ProductDetailModal({
  isOpen,
  onRequestClose,
  headerTitle,
  onUpdateProduct,
  product,
  isSelectedProduct = false, // product selected before
  initialSelectedSkus = [], // if product selected before it has sku selection
  isProductsRemovable = true,
  someSkusUnavailable = false,
  onBackClick,
  onOk = () => {},
  okText = 'Add Item',
  featureType,
  isDraft
}) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { data: productDetail, isLoading } = useGetProductDetail(product.merchantProductId);

  const [selectedSkus, setSelectedSkus] = useState([]);

  const productWithDetail = useMemo(() => {
    if (product && productDetail) {
      return {
        ...product,
        ...productDetail
      };
    }
    return null;
  }, [product, productDetail]);

  useEffect(() => {
    if (isSelectedProduct) {
      setSelectedSkus(initialSelectedSkus);
    } else if (productDetail?.skus?.length === 1) {
      setSelectedSkus(productDetail?.skus);
    }
  }, [productDetail]);

  const showRemoveItemPrompt = () => {
    showNotification({
      title: t('removeItemPromptQuestion', { featureName: featureType }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      onSubmit: () =>
        onOk({
          ...product,
          skus: selectedSkus,
          removeItem: true
        }),
      primaryButtonProps: { color: 'error', children: t('removeItem') },
      secondaryButtonProps: { children: t('keepItem') },
      modalProps: { style: { overlay: { zIndex: Z_INDEX.modal + 10 } } }
    });
  };

  const isPrimaryButtonDisabled = () => {
    if (isSelectedProduct) {
      if (isEqual(initialSelectedSkus, selectedSkus) || selectedSkus?.length === 0) {
        return true;
      }
    } else {
      return selectedSkus?.length === 0;
    }
  };

  if (isLoading || isEmpty(product)) {
    return <AppLoader />;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={headerTitle}
      primaryButtonProps={{
        children: isSelectedProduct ? t('saveChanges') : okText,
        onClick: () => {
          if (isSelectedProduct) {
            onUpdateProduct({
              ...product,
              skus: selectedSkus,
              meta: {
                ...product.meta
              }
            });
            onRequestClose();
          } else {
            onOk({
              ...product,
              meta: {
                ...product.meta
              },
              skus: selectedSkus,
              removeItem: true
            });
          }
        },
        color: 'primary',
        disabled: isPrimaryButtonDisabled()
      }}
      secondaryButtonProps={
        isSelectedProduct
          ? {
              color: 'error',
              variant: 'outlined',
              children: t('removeItem'),
              disabled: !isProductsRemovable,
              onClick: showRemoveItemPrompt
            }
          : {
              children: t('cancel'),
              onClick: onRequestClose
            }
      }
      onBack={onBackClick}
      size="xl"
    >
      <ProductDetail
        product={productWithDetail}
        selectedSkus={selectedSkus}
        setSelectedSkus={setSelectedSkus}
        someSkusUnavailable={someSkusUnavailable}
        isDraft={isDraft}
      />
    </Modal>
  );
}

ProductDetailModal.propTypes = {
  product: PropTypes.object.isRequired,
  isSelectedProduct: PropTypes.bool.isRequired,
  initialSelectedSkus: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  someSkusUnavailable: PropTypes.bool,
  onOk: PropTypes.func,
  okText: PropTypes.string,
  onUpdateProduct: PropTypes.func,
  isProductsRemovable: PropTypes.bool,
  featureType: PropTypes.string,
  isDraft: PropTypes.bool
};

export { ProductDetailModal };
